import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, TrendingUp, Package, Truck, Layers } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Logistics() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [picks, setPicks] = useState({}); // idx -> {vehicle_id, driver_id}

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/logistics/suggestions"); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/drivers").then(({ data }) => setDrivers(data)).catch(() => {});
  }, []);

  const consolidate = async (s, idx) => {
    const vehicle_id = picks[idx]?.vehicle_id || s.suggested_vehicle?.id;
    if (!vehicle_id) { toast.error(t("log.noVehicleErr")); return; }
    try {
      const { data } = await api.post("/logistics/consolidate", {
        order_ids: s.orders.map((o) => o.id), vehicle_id, driver_id: picks[idx]?.driver_id || null,
      });
      toast.success(`${t("log.consolidatedPre")} ${data.consolidated} ${t("log.consolidatedMid")} ${data.vehicle}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="logistics-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Sparkles size={24} className="text-amber-500" /> {t("log.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("log.subtitle")}</p>
        </div>
        <Button variant="outline" onClick={load}>{t("log.refresh")}</Button>
      </div>

      {loading ? <div className="text-slate-400 py-10">{t("log.loading")}</div>
        : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-10 text-center text-slate-400" data-testid="no-suggestions">
            {t("log.noSuggestions")}
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {items.map((s, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4" data-testid="suggestion-card">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400">{t("log.corridor")}</div>
                    <div className="font-display text-lg font-semibold capitalize">{s.origin_region} → {s.destination_region}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-400">{t("log.compatibility")}</div>
                    <div className={`font-display text-2xl font-bold ${s.compatibility_score >= 95 ? "text-emerald-600" : "text-amber-600"}`} data-testid="compat-score">{s.compatibility_score}</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="flex items-center gap-1.5 text-slate-600"><Layers size={15} /> {s.total_orders} {t("log.orders")}</span>
                  <span className="flex items-center gap-1.5 text-slate-600"><Package size={15} /> {s.total_coffins} {t("log.coffins")}</span>
                  <span className="flex items-center gap-1.5 text-emerald-700"><TrendingUp size={15} /> {t("log.savings")} {money(s.estimated_savings, s.currency)}</span>
                </div>
                <div className="text-xs text-slate-500">{t("log.addProfit")} <b className="text-slate-700">{money(s.estimated_additional_profit, s.currency)}</b></div>

                <div className="rounded-lg border border-slate-100 divide-y divide-slate-50">
                  {s.orders.map((o) => (
                    <div key={o.id} className="flex justify-between px-3 py-2 text-sm">
                      <span className="text-slate-700">{o.number} · {o.client_name}</span>
                      <span className="text-slate-400">{o.coffin_count} {t("log.coffinsShort")} · {money(o.transport_cost, s.currency)}</span>
                    </div>
                  ))}
                </div>

                <div className="flex items-end gap-2 flex-wrap">
                  <div className="text-sm flex items-center gap-1.5 text-slate-600">
                    <Truck size={15} /> {s.suggested_vehicle ? `${s.suggested_vehicle.plate} (${t("log.cap")} ${s.suggested_vehicle.capacity_coffins})` : t("log.noVehicle")}
                  </div>
                  <div className="ml-auto flex items-end gap-2">
                    <Select value={picks[idx]?.driver_id || ""} onValueChange={(v) => setPicks({ ...picks, [idx]: { ...picks[idx], driver_id: v } })}>
                      <SelectTrigger className="w-40" data-testid="consolidate-driver"><SelectValue placeholder={t("log.driverOpt")} /></SelectTrigger>
                      <SelectContent>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button onClick={() => consolidate(s, idx)} disabled={!s.suggested_vehicle} className="bg-blue-900 hover:bg-blue-800" data-testid="consolidate-btn">{t("log.consolidate")}</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
